import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { checkEmail, checkWebsite } from '../utils/clientSectionSpecs';

/**
 * The client-record sections that are either a settings form (billing, access,
 * portal) or a simple list (documents, obligations, users, notes).
 *
 * <SettingsSection /> drives the form ones from a field spec so the three share
 * one implementation — they differ only in their fields and endpoint.
 *
 * Field `type`s: text (default) | number | select | textarea | bool | date | email | url | colour.
 * email / url / number / colour are validated inline before saving.
 */

const card = { background: 'var(--cw-surface)', border: '1px solid var(--cw-border)', borderRadius: 10, padding: 18 };
const sectionTitle = { fontWeight: 700, color: 'var(--cw-text)', margin: 0, fontSize: 'var(--cs-text-md)' };

/** Inline validation for one field value → message or null. */
export const fieldError = (f, raw) => {
  const v = raw == null ? '' : String(raw).trim();
  if (f.required && !v) return `Enter ${String(f.label).toLowerCase()}`;
  if (!v) return null;
  if (f.type === 'email') return checkEmail(v);
  if (f.type === 'url') return checkWebsite(v);
  if (f.type === 'colour' && !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return 'Enter a hex colour like #1A80E5';
  if (f.type === 'number') {
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) return 'Enter a number of 0 or more';
    if (f.integer && !Number.isInteger(n)) return 'Enter a whole number';
  }
  return null;
};

const inputFor = (f, value, onChange) => {
  if (f.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {(f.options || []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    );
  }
  if (f.type === 'textarea') {
    return <textarea style={{ minHeight: 70, resize: 'vertical' }} value={value ?? ''} placeholder={f.placeholder || ''} onChange={(e) => onChange(e.target.value)} />;
  }
  const type = { date: 'date', email: 'email', url: 'url' }[f.type] || 'text';
  return (
    <input value={value ?? ''} placeholder={f.placeholder || ''} type={type}
      inputMode={f.type === 'number' ? 'decimal' : f.type === 'email' ? 'email' : f.type === 'url' ? 'url' : undefined}
      onChange={(e) => onChange(f.type === 'number' ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)} />
  );
};

/**
 * A settings form backed by a one-row-per-client endpoint.
 * fields: [{ key, label, type, options?, hint?, full?, group? }]
 */
export const SettingsSection = ({ clientId, endpoint, title, blurb, fields, footerNote }) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/contractor/clients/${clientId}/${endpoint}`);
      setValues(d?.settings || {});
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); setDirty(false); }
  }, [clientId, endpoint]);

  useEffect(() => { load(); }, [load]);

  // Unsaved settings survive a refresh warning.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const set = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setDirty(true); if (errors[k]) setErrors((x) => ({ ...x, [k]: null })); };

  const save = async () => {
    const errs = {};
    fields.forEach((f) => { if (f.type !== 'bool') { const m = fieldError(f, values[f.key]); if (m) errs[f.key] = m; } });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f.key] = values[f.key] ?? (f.type === 'bool' ? 0 : ''); });
      const d = await apiFetch(`/api/contractor/clients/${clientId}/${endpoint}`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      setValues(d?.settings || values);
      setDirty(false);
      toast.success(`${title} saved`);
    } catch (e) { toast.error(`Couldn't save ${String(title).toLowerCase()}: ${apiErrorMessage(e)}`); }
    finally { setSaving(false); }
  };

  // Group fields under optional headings so long forms stay readable.
  const groups = fields.reduce((acc, f) => {
    const g = f.group || '';
    (acc[g] = acc[g] || []).push(f);
    return acc;
  }, {});

  return (
    <section style={card} aria-label={title}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={sectionTitle}>{title}</h3>
          {blurb && <p className="cw-meta" style={{ margin: '3px 0 0' }}>{blurb}</p>}
        </div>
        {!loadError && (
          <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving || !dirty || loading} aria-busy={saving || undefined}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'No unsaved changes'}
          </button>
        )}
      </div>

      {loading ? (
        <div role="status" className="cw-meta" style={{ padding: '22px 0' }}>Loading…</div>
      ) : loadError ? (
        <div style={{ marginTop: 14 }}>
          <ErrorState compact thing={String(title).toLowerCase()} error={loadError} onRetry={load}
            reason={`${apiErrorMessage(loadError)} Nothing is shown so you don't overwrite the saved settings by mistake.`} />
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {Object.entries(groups).map(([g, gf]) => (
            <fieldset key={g || 'main'} style={{ border: 'none', padding: 0, margin: '0 0 18px' }}>
              {g && (
                <legend className="cw-label" style={{ marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--cw-border-light)', width: '100%' }}>{g}</legend>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))', gap: 14 }}>
                {gf.map((f) => (
                  <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : undefined}>
                    {f.type === 'bool' ? (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', minHeight: 44 }}>
                        <input type="checkbox" checked={!!Number(values[f.key] ?? 0)} style={{ marginTop: 3, width: 18, height: 18 }}
                          onChange={(e) => set(f.key, e.target.checked ? 1 : 0)} />
                        <span>
                          <span style={{ fontSize: 'var(--cs-text-base)', color: 'var(--cw-text)', fontWeight: 500 }}>{f.label}</span>
                          {f.hint && <span className="cw-meta" style={{ display: 'block', marginTop: 2 }}>{f.hint}</span>}
                        </span>
                      </label>
                    ) : (
                      <Field label={f.label} hint={f.hint} error={errors[f.key]}>
                        {inputFor(f, values[f.key], (v) => set(f.key, v))}
                      </Field>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
          {footerNote && (
            <p style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 8, padding: '9px 11px', lineHeight: 1.45, margin: 0 }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} aria-hidden="true" />{footerNote}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

/** Inline add / edit form for a list row. */
const RowForm = ({ fields, initial, submitLabel, busy, onSubmit, onCancel }) => {
  const [form, setForm] = useState(initial || {});
  const [errors, setErrors] = useState({});
  const submit = (e) => {
    if (e) e.preventDefault();
    const errs = {};
    fields.forEach((f) => { const m = fieldError(f, form[f.key]); if (m) errs[f.key] = m; });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    onSubmit(form);
  };
  return (
    <form noValidate onSubmit={submit} style={{ border: '1px solid var(--cw-border)', borderRadius: 8, padding: 14, background: 'var(--cw-surface-alt)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
        {fields.map((f) => (
          <Field key={f.key} label={f.label} required={f.required} hint={f.hint} error={errors[f.key]} style={f.full ? { gridColumn: '1 / -1' } : undefined}>
            {inputFor(f, form[f.key], (v) => { setForm((s) => ({ ...s, [f.key]: v })); if (errors[f.key]) setErrors((x) => ({ ...x, [f.key]: null })); })}
          </Field>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={() => onCancel(JSON.stringify(form) !== JSON.stringify(initial || {}))} disabled={busy}>Cancel</button>
        <button type="submit" className="cw-btn cw-btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Saving…' : submitLabel}</button>
      </div>
    </form>
  );
};

/**
 * A list-backed section: load rows, add via a small inline form, edit and delete rows.
 * renderRow decides how each item looks; addFields drives the add form.
 *
 * Editing:
 *   editMode="put"      → PUT /:rowId with the editFields (the endpoint supports updates)
 *   editMode="patch"    → PATCH /:rowId with the editFields (edit in place, audited)
 *   editMode="replace"  → no update endpoint: create the edited row, then remove the
 *                         old one (created first so nothing is lost if a step fails)
 *   editFields          → which fields the edit form shows (default: addFields)
 *   rowNoun             → "rate", "document"… used in confirmations
 */
export const ListSection = ({
  clientId, endpoint, listKey, title, blurb, addLabel = 'Add',
  addFields, renderRow, emptyIcon, emptyTitle, emptyBlurb, onChanged,
  editMode, editFields, rowNoun = 'entry', rowLabel,
}) => {
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const base = `/api/contractor/clients/${clientId}/${endpoint}`;
  const labelOf = (r) => (rowLabel ? rowLabel(r) : (r.title || r.name || r.task || r.email || rowNoun));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(base);
      setRows(Array.isArray(d?.[listKey]) ? d[listKey] : []);
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, [base, listKey]);

  useEffect(() => { load(); }, [load]);

  const discard = async (dirty) => {
    if (dirty && !(await confirm({ title: 'Discard changes?', body: "What you've typed hasn't been saved.", confirmLabel: 'Discard', tone: 'danger' }))) return false;
    return true;
  };

  const add = async (form) => {
    setSaving(true);
    try {
      await apiFetch(base, { method: 'POST', body: JSON.stringify(form) });
      toast.success(`${labelOf(form)} added`);
      setAdding(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(`Couldn't add that: ${apiErrorMessage(e)}`); }
    finally { setSaving(false); }
  };

  const edit = async (row, form) => {
    setSaving(true);
    try {
      if (editMode === 'put' || editMode === 'patch') {
        const body = {};
        (editFields || addFields || []).forEach((f) => { body[f.key] = form[f.key] ?? ''; });
        await apiFetch(`${base}/${row.id}`, { method: editMode === 'patch' ? 'PATCH' : 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch(base, { method: 'POST', body: JSON.stringify(form) });
        try { await apiFetch(`${base}/${row.id}`, { method: 'DELETE' }); }
        catch (e) { toast.error(`The updated ${rowNoun} was saved, but the old one couldn't be removed: ${apiErrorMessage(e)}. Remove it by hand.`); }
      }
      toast.success(`${labelOf({ ...row, ...form })} updated`);
      setEditingId(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(`Couldn't save the change: ${apiErrorMessage(e)}`); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    const ok = await confirm({ title: `Remove ${labelOf(row)}?`, body: `This ${rowNoun} will be removed from the client record.`, confirmLabel: `Remove ${rowNoun}`, tone: 'danger' });
    if (!ok) return;
    try {
      await apiFetch(`${base}/${row.id}`, { method: 'DELETE' });
      toast.success(`${labelOf(row)} removed`);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(`Couldn't remove it: ${apiErrorMessage(e)}`); }
  };

  const patch = async (rowId, body) => {
    const before = rows;
    setRows((list) => list.map((r) => (r.id === rowId ? { ...r, ...body } : r)));
    try {
      await apiFetch(`${base}/${rowId}`, { method: 'PUT', body: JSON.stringify(body) });
      await load();
    } catch (e) { setRows(before); toast.error(`Couldn't update it: ${apiErrorMessage(e)}`); }
  };

  const fieldsForEdit = editFields || addFields || [];
  const toForm = (r) => Object.fromEntries(fieldsForEdit.map((f) => [f.key, f.type === 'date' ? String(r[f.key] || '').slice(0, 10) : (r[f.key] ?? '')]));

  return (
    <section style={card} aria-label={title}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <h3 style={sectionTitle}>{title}</h3>
          {blurb && <p className="cw-meta" style={{ margin: '3px 0 0' }}>{blurb}</p>}
        </div>
        {!adding && !loadError && (
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => { setEditingId(null); setAdding(true); }}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> {addLabel}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ marginBottom: 14 }}>
          <RowForm fields={addFields || []} initial={{}} submitLabel={addLabel} busy={saving}
            onSubmit={add} onCancel={async (dirty) => { if (await discard(dirty)) setAdding(false); }} />
        </div>
      )}

      {loading && !rows.length ? (
        <div role="status" className="cw-meta" style={{ padding: '20px 0' }}>Loading…</div>
      ) : loadError ? (
        <ErrorState compact thing={String(title).toLowerCase()} error={loadError} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState compact icon={emptyIcon} title={emptyTitle} body={emptyBlurb} />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {rows.map((r) => (
            <li key={r.id}>
              {editingId === r.id ? (
                <RowForm fields={fieldsForEdit} initial={toForm(r)} submitLabel="Save changes" busy={saving}
                  onSubmit={(form) => edit(r, form)} onCancel={async (dirty) => { if (await discard(dirty)) setEditingId(null); }} />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, border: '1px solid var(--cw-border)', borderRadius: 8, padding: '11px 14px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: '1 1 220px' }}>{renderRow(r, { patch })}</div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {editMode && (
                      <button type="button" className="cw-icon-btn" aria-label={`Edit ${labelOf(r)}`} title="Edit"
                        onClick={() => { setAdding(false); setEditingId(r.id); }}>
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" className="cw-icon-btn" aria-label={`Remove ${labelOf(r)}`} title="Remove"
                      onClick={() => remove(r)} style={{ color: 'var(--cw-danger)' }}>
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SettingsSection;
